import { moment } from '@seiue/moment'
import { Badge, DropdownButton, DropdownButtonItem, Ellipsis } from '@seiue/ui'
import { safeParseJSON } from '@seiue/util'
import { Progress } from 'antd'
import React, { FC } from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import { TagStatus } from 'packages/components/TagStatus'
import { Notification, StatusEnum } from 'packages/sdks-next/growp'
import { AnyType } from 'packages/types'

import { getTheme } from '@/utils/theme'

import { getNoticeRichtext } from '../NoticeDetail'

import { noticeStatusToLabel, noticeStatusToTagStatus } from './utils'

/**
 * 通知列表项
 *
 * @param param0 - 参数
 * @param param0.notification - 通知
 * @param param0.itemClick - 点击事件
 * @param param0.actionItems - 操作
 * @param param0.showSenderName - 显示发件人姓名
 * @param param0.readonly - 是否只读
 * @returns 组件
 */
export const NotificationItem: FC<{
  notification: Notification
  itemClick?: () => void
  actionItems: DropdownButtonItem[]
  showSenderName?: boolean
  readonly?: boolean
}> = ({ notification, itemClick, actionItems, showSenderName, readonly }) => {
  const theme = getTheme()

  return (
    <ItemWrapper
      onClick={itemClick}
      $clickable={!!itemClick}
    >
      <Header>
        <Badge
          dot={
            notification.currentRecever &&
            !notification.currentRecever?.isReaded
          }
          offset={[8, 12]}
          size="small"
        >
          <Title
            style={{
              maxWidth: readonly
                ? 'calc(100vw - 290px)'
                : 'calc(100vw - 460px)',
            }}
          >
            <Ellipsis text={notification.title || '-'} />
          </Title>
        </Badge>
        {readonly ? null : (
          <Static>
            {notification.status === StatusEnum.Published ? (
              <StaticProgress>
                <Progress
                  percent={
                    notification.rids?.length
                      ? ((notification.readedCount || 0) /
                          notification.rids?.length) *
                        100
                      : 0
                  }
                  strokeWidth={20}
                  width={14}
                  type="circle"
                  style={{
                    width: 16,
                    height: 16,
                    position: 'relative',
                    top: '-4px',
                  }}
                  showInfo={false}
                  trailColor={theme.brand._4}
                  strokeColor={theme.brand._1}
                />
                <StaticText>{`${notification.readedCount || 0} / ${
                  notification.rids?.length
                } 已读`}</StaticText>
              </StaticProgress>
            ) : null}
            <TagStatus
              status={
                noticeStatusToTagStatus.get(notification.status) as AnyType
              }
              text={noticeStatusToLabel.get(notification.status)}
            />
            <DropdownButton
              items={actionItems}
              showArrow={false}
            >
              <GrowpIcon
                name="Gengduo"
                onClick={(e) => {
                  e.stopPropagation()
                }}
              />
            </DropdownButton>
          </Static>
        )}
      </Header>
      <Ellipsis
        text={getNoticeRichtext(
          (notification.setting as { plainText?: string })?.['plainText'] ||
            '-',
          safeParseJSON(notification.currentRecever?.variables || '') || {}
        )}
        lineClamp={2}
        style={{
          color: '#455766',
        }}
        tooltipProps={{
          visible: false,
        }}
      />
      {showSenderName ? (
        <Footer>
          <Time>{`${
            notification.publishedAt
              ? moment(notification.publishedAt).format('YYYY-MM-DD HH:mm')
              : notification.status === StatusEnum.Withdrawn
              ? '-'
              : '暂不发送'
          }`}</Time>
          <Time>{notification.senderName ?? '-'}</Time>
        </Footer>
      ) : (
        <Time>{`发送时间：${
          notification.publishedAt
            ? moment(notification.publishedAt).format('YYYY-MM-DD HH:mm')
            : notification.status === StatusEnum.Withdrawn
            ? '-'
            : '暂不发送'
        }`}</Time>
      )}
    </ItemWrapper>
  )
}

const Footer = styled.div`
  display: flex;
  gap: 8px;
`

const ItemWrapper = styled.div<{ $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 16px 0px;
  gap: 8px;
  border-bottom: 1px solid ${(p) => p.theme.border.greyNormal};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const Title = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${(p) => p.theme.text._1};
  max-width: 400px;
`

const Time = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(p) => p.theme.text._3};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Static = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const StaticProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const StaticText = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${(p) => p.theme.text._2};
`
